import throttle from 'lodash/throttle';

import { writeHtmlToOsClipboard } from '@canvas/clipboard';
import { type PasteOrDropPayload } from '@canvas/clipboard/types';
import { type CanvasHTMLElement } from '@canvas/input-types/base-event-types';
import { type PointerButton } from '@canvas/input-types/pointer-events-types';

import { getInputTarget } from '../input-target/get-input-target';
import {
  type Capture,
  type CaptureListener,
  type CaptureType,
  type IdCallback,
} from '../input-types';

// If the document's active element matches this selector, we should allow native copy
const allowCopySelectorActive = 'input, text, [contenteditable="true"]';

// If the copy event's target or any of its parents match this selector, we should
// allow native copy
const allowCopySelectorClosest = '[data-is-text-selectable]';

// Returns true if the copy event should be allowed to proceed natively
export function shouldAllowCopy(target: EventTarget | null) {
  const activeElement =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const targetElement = target instanceof HTMLElement ? target : null;

  const allowCopyActiveElement = activeElement?.matches(allowCopySelectorActive) ?? false;
  const allowCopyClosestElement =
    targetElement?.closest(allowCopySelectorClosest) != null;

  return allowCopyActiveElement || allowCopyClosestElement;
}

export const setupClipboardEvents = (
  root: CanvasHTMLElement,
  id: IdCallback,
  onCapture: (capture: Capture) => void,
  captureContext: {
    x?: number;
    y?: number;
    button?: PointerButton;
  },
  listeners: Map<CaptureType, CaptureListener>
) => {
  const onCut = (evt: ClipboardEvent) => {
    if (shouldAllowCopy(evt.target)) {
      return false;
    }

    if (evt.clipboardData == null) {
      return;
    }

    const copied = onCopy(evt);

    if (!copied) {
      return;
    }

    onCapture({
      id: id(),
      type: 'delete',
      target: getInputTarget(evt.target),
      properties: {},
    });
  };

  const onCopy = (evt: ClipboardEvent): boolean => {
    // We should defer to the native clipboard when input or textareas are active
    if (shouldAllowCopy(evt.target)) {
      return false;
    }

    evt.preventDefault();

    const copyPayload = listeners.get('copy')?.();
    if (!copyPayload) {
      return false;
    }

    if (evt.clipboardData) {
      // For real clipboard events from the browser, use clipboardData
      evt.clipboardData.setData('text/html', copyPayload);
    } else {
      // For synthetic events (e.g., from database iframe forwarding),
      // use the Clipboard API to write directly to the OS clipboard
      // It is technically impossible to use .clipboardData.setData as
      // synthetic events cannot set the clipboard data to that of the OS
      writeHtmlToOsClipboard(copyPayload);
    }

    return true;
  };

  /**
   * We throttle paste events since some OS's will let the user
   * hold down the shortcut, causing a rapid burst of paste events that
   * can be dangerous to our system.
   */
  const onPasteCapture = throttle(
    (
      evt: Event,
      pastePayload: PasteOrDropPayload,
      position: { x?: number; y?: number } = { x: captureContext.x, y: captureContext.y }
    ) => {
      {
        if (captureContext.button === 'middle') {
          // On some linux distros, the middle button triggers an unwanted paste.
          return;
        }
        onCapture({
          id: id(),
          type: 'paste',
          target: getInputTarget(evt.target),
          properties: {
            clipboardContent: pastePayload,
            x: position.x ?? globalThis.innerWidth / 2,
            y: position.y ?? globalThis.innerHeight / 2,
          },
        });
      }
    },
    200,
    { leading: true, trailing: false }
  );

  const onPaste = (evt: ClipboardEvent) => {
    // Allow native paste behaviour when focus is on an input, contenteditable,
    // or an element explicitly marked as text-selectable (e.g. editors)
    if (shouldAllowCopy(evt.target)) {
      return;
    }

    if (evt.clipboardData == null) {
      return;
    }

    listeners
      .get('paste')?.(evt)
      .then((pastePayload: PasteOrDropPayload | undefined) => {
        if (pastePayload) {
          onPasteCapture(evt, pastePayload);
        }
      });
  };

  root.addEventListener('copy', onCopy);
  root.addEventListener('cut', onCut);
  root.addEventListener('paste', onPaste);

  return () => {
    root.removeEventListener('copy', onCopy);
    root.removeEventListener('cut', onCut);
    root.removeEventListener('paste', onPaste);
  };
};
